/**
 * Customer Portal (UI) - reports route.
 *
 * Current focus:
 * - Webhooks report powered by reporting-worker scatter/gather reads.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchWebhookAuditReport,
  fetchWebhookJobDetails,
  retryWebhookJob
} from '../features/api/client';
import type {
  WebhookAuditReportItem,
  WebhookAuditStatus,
  WebhookJobAttempt,
  WebhookJobDetailApiResponse
} from '../types/portal';
import { formatPortalDateTime, type TimeDisplayMode } from '../utils/datetime';
import {
  resolveParentMessageId,
  resolveParentMessageType,
  selectDisplayedWebhookPayload
} from './webhook-payload';

const DEFAULT_LIMIT = 100;
const WEBHOOK_TYPE_OPTIONS = ['', 'dlr', 'inbound_message', 'keyword_action', 'opt_out', 'message_sent_upstream'] as const;
const STATUS_OPTIONS: Array<WebhookAuditStatus | ''> = ['', 'PENDING', 'IN_FLIGHT', 'DELIVERED', 'FAILED'];

type SmsMessageDetail = {
  message: {
    prontoMessageId: string;
    fromNumber: string;
    toNumber: string;
    status: string;
    createdAt: string;
    segments: number | null;
    type?: 'SMS' | 'MMS';
    renderedBody:
      | string
      | Array<{
          type: 'text' | 'media_url' | 'binary_media' | 'media_r2';
          value: string;
        }>
      | null;
  } | null;
  lifecycle: Array<{
    eventType: string;
    timestamp: string;
    stageStatus: string | null;
    httpStatus: number | null;
    attemptCount: number | null;
  }>;
};

function normalizeDateInputToIso(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.toISOString();
}

function statusBadgeClass(status: WebhookAuditStatus): string {
  if (status === 'DELIVERED') {
    return 'badge bg-success text-success-fg';
  }
  if (status === 'FAILED') {
    return 'badge bg-danger text-danger-fg';
  }
  if (status === 'IN_FLIGHT') {
    return 'badge bg-azure text-azure-fg';
  }
  return 'badge bg-secondary text-secondary-fg';
}

function resolveMessageDirectionFromWebhookType(webhookType: string): 'inbound' | 'outbound' {
  const normalized = webhookType.trim().toLowerCase();
  if (normalized === 'inbound_message' || normalized === 'keyword_action' || normalized === 'opt_out') {
    return 'inbound';
  }
  return 'outbound';
}

type ReportsRouteProps = {
  timeDisplayMode: TimeDisplayMode;
};

type MessageExportStatus = 'pending' | 'running' | 'completed' | 'failed';

type MessageExportJob = {
  exportId: string;
  source: 'api' | 'web';
  description: string;
  requestedAt: string;
  status: MessageExportStatus;
  rowCount: number | null;
  completedAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  downloadUrl: string | null;
  downloadUrlExpiresAt: string | null;
};

const REPORT_CARDS = [
  {
    to: '/reports/webhooks',
    title: 'Webhooks Report',
    description: 'Audit webhook delivery attempts, retry behavior, and payloads.'
  }
] as const;

function ReportsExportJobsTab({ timeDisplayMode }: ReportsRouteProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<MessageExportJob[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/sms/exports?limit=50', { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Request failed (${response.status}) loading export jobs`);
      }
      const payload = (await response.json()) as
        | { SchemaVersion: string; RequestedAt: string; Data?: { Items?: MessageExportJob[] } }
        | { items?: MessageExportJob[] };
      const itemsPayload =
        payload && 'Data' in payload && payload.Data
          ? payload.Data.Items ?? []
          : 'items' in payload
            ? payload.items ?? []
            : [];
      setItems(itemsPayload);
    } catch (loadError) {
      setItems([]);
      setError(loadError instanceof Error ? loadError.message : String(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const hasPending = items.some((item) => item.status === 'pending' || item.status === 'running');
    if (!hasPending) {
      return;
    }
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [items, load]);

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="card-title mb-0">Export Jobs</h3>
        <div className="d-flex gap-2">
          <Link className="btn btn-ghost-secondary btn-sm" to="/messages">
            Create export from Messages
          </Link>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => void load()} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="text-secondary small mb-3">
          Asynchronous export jobs requested from platform surfaces. Pending/running jobs refresh automatically.
        </div>
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <div className="table-responsive">
          <table className="table table-sm table-vcenter mb-0">
            <thead>
              <tr>
                <th>Export ID</th>
                <th>Source</th>
                <th>Description</th>
                <th>Requested</th>
                <th>Status</th>
                <th>Rows</th>
                <th>Completed</th>
                <th>Error</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 ? (
                <tr>
                  <td className="text-secondary" colSpan={9}>
                    No export jobs found.
                  </td>
                </tr>
              ) : null}
              {items.map((job) => (
                <tr key={job.exportId}>
                  <td className="text-monospace small">{job.exportId}</td>
                  <td className="text-uppercase">{job.source}</td>
                  <td>{job.description}</td>
                  <td>{formatPortalDateTime(job.requestedAt, timeDisplayMode)}</td>
                  <td className="text-uppercase">{job.status}</td>
                  <td>{job.rowCount ?? '\u2014'}</td>
                  <td>{job.completedAt ? formatPortalDateTime(job.completedAt, timeDisplayMode) : '\u2014'}</td>
                  <td className="portal-wrap small">{job.errorMessage ?? '\u2014'}</td>
                  <td>
                    {job.downloadUrl ? (
                      <a
                        className="btn btn-sm btn-ghost-primary"
                        href={(() => {
                          try {
                            const parsed = new URL(job.downloadUrl);
                            return `/api/sms/exports/${encodeURIComponent(job.exportId)}/download${parsed.search}`;
                          } catch {
                            return `/api/sms/exports/${encodeURIComponent(job.exportId)}/download`;
                          }
                        })()}
                      >
                        Download
                      </a>
                    ) : (
                      <span className="text-secondary small">Not ready</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function ReportsIndexRoute({ timeDisplayMode }: ReportsRouteProps) {
  const [activeTab, setActiveTab] = useState<'reports' | 'exports'>('reports');
  return (
    <>
      <div className="page-pretitle">Analytics</div>
      <h2 className="page-title mb-3">Reports</h2>
      <div className="card mb-3">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                type="button"
                className={`nav-link${activeTab === 'reports' ? ' active' : ''}`}
                onClick={() => setActiveTab('reports')}
              >
                Reports
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                type="button"
                className={`nav-link${activeTab === 'exports' ? ' active' : ''}`}
                onClick={() => setActiveTab('exports')}
              >
                Export Jobs
              </button>
            </li>
          </ul>
        </div>
      </div>
      {activeTab === 'reports' ? (
        <div className="row row-cards">
          {REPORT_CARDS.map((report) => (
            <div className="col-sm-6 col-lg-4" key={report.to}>
              <Link to={report.to} className="card card-link cursor-pointer h-100">
                <div className="card-body">
                  <h3 className="card-title mb-2">{report.title}</h3>
                  <div className="text-secondary">{report.description}</div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <ReportsExportJobsTab timeDisplayMode={timeDisplayMode} />
      )}
      <div className="row row-cards">
      </div>
    </>
  );
}

export function WebhooksReportRoute({ timeDisplayMode }: ReportsRouteProps) {
  const [items, setItems] = useState<WebhookAuditReportItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<WebhookAuditReportItem | null>(null);
  const [jobDetails, setJobDetails] = useState<WebhookJobDetailApiResponse | null>(null);
  const [jobDetailsLoading, setJobDetailsLoading] = useState<boolean>(false);
  const [retrying, setRetrying] = useState<boolean>(false);
  const [messageDetail, setMessageDetail] = useState<SmsMessageDetail | null>(null);
  const [messageDetailLoading, setMessageDetailLoading] = useState<boolean>(false);

  const [webhookTypeFilter, setWebhookTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<WebhookAuditStatus | ''>('');
  const [fromFilter, setFromFilter] = useState<string>('');
  const [toFilter, setToFilter] = useState<string>('');

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWebhookAuditReport({
        webhookType: webhookTypeFilter || undefined,
        status: statusFilter || undefined,
        fromIso: normalizeDateInputToIso(fromFilter),
        toIso: normalizeDateInputToIso(toFilter),
        limit: DEFAULT_LIMIT
      });
      setItems(response.items);
      setNextCursor(response.nextCursor);
    } catch (loadError) {
      setItems([]);
      setNextCursor(null);
      setError(loadError instanceof Error ? loadError.message : String(loadError));
    } finally {
      setLoading(false);
    }
  }, [fromFilter, statusFilter, toFilter, webhookTypeFilter]);

  const loadNextPage = useCallback(async () => {
    if (!nextCursor) {
      return;
    }
    setLoadingMore(true);
    setError(null);
    try {
      const response = await fetchWebhookAuditReport({
        webhookType: webhookTypeFilter || undefined,
        status: statusFilter || undefined,
        fromIso: normalizeDateInputToIso(fromFilter),
        toIso: normalizeDateInputToIso(toFilter),
        limit: DEFAULT_LIMIT,
        cursor: nextCursor
      });
      setItems((prev) => [...prev, ...response.items]);
      setNextCursor(response.nextCursor);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError));
    } finally {
      setLoadingMore(false);
    }
  }, [fromFilter, nextCursor, statusFilter, toFilter, webhookTypeFilter]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  const totalRowsLabel = useMemo(() => `${items.length.toLocaleString()} rows`, [items.length]);

  const openWebhookJobModal = useCallback(async (item: WebhookAuditReportItem) => {
    setSelectedJob(item);
    setJobDetails(null);
    setJobDetailsLoading(true);
    setError(null);
    try {
      const details = await fetchWebhookJobDetails(item.id, item.shardName);
      setJobDetails(details);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError));
    } finally {
      setJobDetailsLoading(false);
    }
  }, []);

  const openParentMessage = useCallback(async (messageId: string, webhookType: string, messageType: 'SMS' | 'MMS') => {
    setMessageDetailLoading(true);
    setMessageDetail(null);
    setError(null);
    void webhookType;
    void messageType;
    try {
      const response = await fetch(
        `/api/sms/messages/${encodeURIComponent(messageId)}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error(`Request failed (${response.status}) loading parent message`);
      }
      setMessageDetail((await response.json()) as SmsMessageDetail);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError));
    } finally {
      setMessageDetailLoading(false);
    }
  }, []);

  const handleRetrySelectedJob = useCallback(async () => {
    if (!selectedJob) {
      return;
    }
    setRetrying(true);
    setError(null);
    try {
      const retryResult = await retryWebhookJob(selectedJob.id, selectedJob.shardName);
      await loadFirstPage();
      const targetJobId =
        retryResult.retried && typeof retryResult.newJobId === 'number' && Number.isFinite(retryResult.newJobId)
          ? Math.floor(retryResult.newJobId)
          : selectedJob.id;
      const details = await fetchWebhookJobDetails(targetJobId, selectedJob.shardName);
      setJobDetails(details);
      if (details.job) {
        setSelectedJob(details.job);
      }
    } catch (retryError) {
      setError(retryError instanceof Error ? retryError.message : String(retryError));
    } finally {
      setRetrying(false);
    }
  }, [loadFirstPage, selectedJob]);

  const displayedAttempts: WebhookJobAttempt[] = useMemo(() => jobDetails?.attempts ?? [], [jobDetails]);
  const displayedJob = useMemo(() => jobDetails?.job ?? selectedJob, [jobDetails, selectedJob]);
  const displayedPayload = useMemo(() => {
    return selectDisplayedWebhookPayload(displayedJob, displayedAttempts);
  }, [displayedAttempts, displayedJob]);
  const displayedPayloadText = useMemo(() => {
    if (!displayedPayload || Object.keys(displayedPayload).length === 0) {
      return null;
    }
    return JSON.stringify(displayedPayload, null, 2);
  }, [displayedPayload]);
  const displayedParentMessageId = useMemo(() => resolveParentMessageId(displayedJob), [displayedJob]);
  const displayedParentMessageType = useMemo(() => resolveParentMessageType(displayedJob), [displayedJob]);

  return (
    <>
      <div className="page-pretitle">Analytics</div>
      <h2 className="page-title mb-3">Reports</h2>
      <div className="mb-3">
        <Link to="/reports" className="link-secondary">
          All reports
        </Link>
      </div>
      <div className="row row-cards">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Webhooks Report</h3>
              <div className="card-actions text-secondary">{loading ? 'Loading...' : totalRowsLabel}</div>
            </div>
            <div className="card-body">
              <div className="row g-2 mb-3">
                <div className="col-sm-6 col-lg-3">
                  <label className="form-label">Webhook Type</label>
                  <select
                    className="form-select form-select-sm"
                    value={webhookTypeFilter}
                    onChange={(event) => setWebhookTypeFilter(event.target.value)}
                  >
                    {WEBHOOK_TYPE_OPTIONS.map((value) => (
                      <option key={value || 'all'} value={value}>
                        {value ? value : 'All types'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select form-select-sm"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value as WebhookAuditStatus | '')}
                  >
                    {STATUS_OPTIONS.map((value) => (
                      <option key={value || 'all'} value={value}>
                        {value ? value : 'All statuses'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <label className="form-label">From</label>
                  <input
                    className="form-control form-control-sm"
                    type="datetime-local"
                    value={fromFilter}
                    onChange={(event) => setFromFilter(event.target.value)}
                  />
                </div>
                <div className="col-sm-6 col-lg-3">
                  <label className="form-label">To</label>
                  <input
                    className="form-control form-control-sm"
                    type="datetime-local"
                    value={toFilter}
                    onChange={(event) => setToFilter(event.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex gap-2 mb-3">
                <button className="btn btn-primary btn-sm" type="button" onClick={() => void loadFirstPage()} disabled={loading}>
                  Apply filters
                </button>
                <button
                  className="btn btn-ghost-secondary btn-sm"
                  type="button"
                  onClick={() => {
                    setWebhookTypeFilter('');
                    setStatusFilter('');
                    setFromFilter('');
                    setToFilter('');
                  }}
                  disabled={loading}
                >
                  Clear filters
                </button>
              </div>

              {error ? <div className="alert alert-danger py-2">{error}</div> : null}

              <div className="table-responsive">
                <table className="table table-sm card-table table-vcenter">
                  <thead>
                    <tr>
                      <th>Created</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Profile</th>
                      <th>Parent Message</th>
                      <th>Attempts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && items.length === 0 ? (
                      <tr>
                        <td className="text-secondary" colSpan={6}>
                          No webhook audit events matched the current filters.
                        </td>
                      </tr>
                    ) : null}
                    {items.map((item) => (
                      <tr
                        key={`${item.shardName}:${item.id}`}
                        className="cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() => void openWebhookJobModal(item)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            void openWebhookJobModal(item);
                          }
                        }}
                      >
                        <td>{formatPortalDateTime(item.createdAt, timeDisplayMode)}</td>
                        <td className="text-secondary">{item.webhookType}</td>
                        <td>
                          <span className={statusBadgeClass(item.status)}>{item.status}</span>
                        </td>
                        <td>{item.profileId ?? '-'}</td>
                        <td>{resolveParentMessageId(item) ?? '-'}</td>
                        <td>{item.attemptCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-end mt-3">
                <button
                  className="btn btn-outline-primary btn-sm"
                  type="button"
                  disabled={!nextCursor || loadingMore || loading}
                  onClick={() => void loadNextPage()}
                >
                  {loadingMore ? 'Loading more...' : nextCursor ? 'Load more' : 'No more rows'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(selectedJob !== null || jobDetailsLoading) && (
        <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{jobDetailsLoading ? 'Loading...' : 'Webhook Job Detail'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => {
                    setSelectedJob(null);
                    setJobDetails(null);
                    setMessageDetail(null);
                  }}
                />
              </div>
              <div className="modal-body">
                {jobDetailsLoading ? <div className="text-secondary">Loading...</div> : null}
                {displayedJob && (
                  <div>
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-6">
                        <dl className="row mb-3">
                          <dt className="col-sm-3">Job ID</dt>
                          <dd className="col-sm-9">{displayedJob.id}</dd>
                          <dt className="col-sm-3">Status</dt>
                          <dd className="col-sm-9">
                            <span className={statusBadgeClass(displayedJob.status)}>{displayedJob.status}</span>
                          </dd>
                          <dt className="col-sm-3">Webhook Type</dt>
                          <dd className="col-sm-9">{displayedJob.webhookType}</dd>
                          <dt className="col-sm-3">Parent Message</dt>
                          <dd className="col-sm-9">{displayedParentMessageId ?? '-'}</dd>
                          <dt className="col-sm-3">Attempts</dt>
                          <dd className="col-sm-9">{displayedJob.attemptCount}</dd>
                        </dl>

                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            type="button"
                            disabled={!displayedParentMessageId || messageDetailLoading}
                            onClick={() => {
                              if (displayedParentMessageId) {
                                void openParentMessage(displayedParentMessageId, displayedJob.webhookType, displayedParentMessageType);
                              }
                            }}
                          >
                            {messageDetailLoading ? 'Loading parent message...' : 'Parent Message'}
                          </button>
                          <button
                            className="btn btn-primary btn-sm"
                            type="button"
                            disabled={displayedJob.status !== 'FAILED' || retrying}
                            onClick={() => void handleRetrySelectedJob()}
                          >
                            {retrying ? 'Retrying...' : 'Retry Webhook'}
                          </button>
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <h6 className="mb-2">Webhook Payload</h6>
                        <div
                          className="bg-light border rounded p-2 overflow-auto"
                          style={{ maxHeight: 'min(40vh, 400px)' }}
                          aria-label="Webhook payload"
                        >
                          {displayedPayloadText ? (
                            <pre className="mb-0 small" style={{ fontFamily: 'var(--tblr-font-monospace, monospace)' }}>
                              {displayedPayloadText}
                            </pre>
                          ) : (
                            <div className="text-secondary small">No payload stored</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <h6 className="mb-2">Attempt History</h6>
                    <div className="table-responsive">
                      <table className="table table-sm card-table table-vcenter">
                        <thead>
                          <tr>
                            <th className="text-center">#</th>
                            <th className="text-center">Attempted At</th>
                            <th className="text-center">HTTP Status</th>
                            <th className="text-center">Will Retry</th>
                            <th className="text-center">Terminal</th>
                            <th className="text-center">Error / Response</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedAttempts.length === 0 ? (
                            <tr>
                              <td className="text-secondary text-center" colSpan={6}>
                                No attempts recorded yet.
                              </td>
                            </tr>
                          ) : null}
                          {displayedAttempts.map((attempt) => (
                            <tr key={attempt.id}>
                              <td className="text-center">{attempt.attemptNumber}</td>
                              <td className="text-center">{formatPortalDateTime(attempt.attemptedAt, timeDisplayMode)}</td>
                              <td className="text-center">{attempt.statusCode ?? '-'}</td>
                              <td className="text-center">{attempt.willRetry ? 'Yes' : 'No'}</td>
                              <td className="text-center">{attempt.isTerminal ? 'Yes' : 'No'}</td>
                              <td className="portal-wrap text-center">{attempt.errorMessage ?? attempt.responseBody ?? '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {(messageDetail !== null || messageDetailLoading) && (
        <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{messageDetailLoading ? 'Loading...' : 'Message Detail'}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setMessageDetail(null)} />
              </div>
              <div className="modal-body">
                {messageDetailLoading ? (
                  <div className="text-secondary">Loading...</div>
                ) : messageDetail?.message ? (
                  <dl className="row mb-0">
                    <dt className="col-sm-3">Message ID</dt>
                    <dd className="col-sm-9">{messageDetail.message.prontoMessageId}</dd>
                    <dt className="col-sm-3">From</dt>
                    <dd className="col-sm-9">{messageDetail.message.fromNumber}</dd>
                    <dt className="col-sm-3">To</dt>
                    <dd className="col-sm-9">{messageDetail.message.toNumber}</dd>
                    <dt className="col-sm-3">Status</dt>
                    <dd className="col-sm-9">{messageDetail.message.status}</dd>
                    <dt className="col-sm-3">Created</dt>
                    <dd className="col-sm-9">{formatPortalDateTime(messageDetail.message.createdAt, timeDisplayMode)}</dd>
                  </dl>
                ) : (
                  <div className="text-secondary">Message not found</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
